import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Checkbox from '@axa-japan/design-system-react/CheckboxGroupField';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon, WarningIcon } from '@axa-japan/design-system-react/Icons';
import Message from '@axa-japan/design-system-react/Message';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import DocumentUpload from '../components/DocumentUpload/DocumentUpload';
import ErrorMessage from '../components/ErrorMessage';
import CustomModal from '../components/common/CustomModal';
import useCreateUploadAction from '../hooks/useCreateUploadAction';
import { useGetDocuments } from '../hooks/useGetDocuments';
import useInvalidateAccess from '../hooks/useInvalidateAccess';
import { queryClient } from '../libs/queryClient';
import { useUserContext } from '../providers/UserProvider';
import { DOCUMENT_STATUS, DOCUMENT_UPLOAD_PROGRESS } from '../types/session';
import { FILE_DEFAULT_NAME } from '../utils/fileUtils';
import { DocumentType, FormDataType } from '../utils/formUtils';
import { CheckboxContainer } from './AgreementPage';

type UploadFilePageForm = {
  sessionId?: number;
  receptionNo: string;
  formType: string;
  representativeDocs: string[];
  documentsSelected: string[];
  uploadType: string;
  failedUploadDocuments: DocumentType[];
  pendingUploadDocuments: DocumentType[];
  uploadConfirmation: boolean;
};

const UploadFilePageSchema = z.object({
  pendingUploadDocuments: z
    .array(
      z.object({
        documentId: z.string(),
        name: z.string(),
        guidelines: z.string(),
        files: z
          .array(
            z.object({
              fileId: z.string(),
              content: z.instanceof(File),
            }),
          )
          .refine((files) => {
            // Check if at least one file is valid (not a placeholder)
            const hasValidFile = files.some((file) => file.content.name !== FILE_DEFAULT_NAME);
            // Check if all files are placeholders
            const allDefaults = files.every((file) => file.content.name === FILE_DEFAULT_NAME);
            return hasValidFile && !allDefaults;
          }),
      }),
    )
    .min(1, { message: '書類の撮影・ファイル選択をしてください。' }),
  uploadConfirmation: z.boolean().refine((value) => value === true, 'チェックしてください。'),
});

type UploadFilePageProps = {
  initialData: UploadFilePageForm;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  onNext: (data: UploadFilePageForm) => void;
  onBack: () => void;
};

const UploadFilePage: React.FC<UploadFilePageProps> = ({ onBack, onNext, initialData, setFormData }) => {
  const [hasError, setHasError] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isPartialErrorModalOpen, setIsPartialErrorModalOpen] = useState(false);
  const [documentFailedList, setDocumentFailedList] = useState<(DocumentType | undefined)[]>();
  const [qualityErrorByDocument, setQualityErrorByDocument] = useState<Record<string, boolean>>({});

  const handleDocumentQualityCheckChange = (documentId: string, hasQualityError: boolean) => {
    setQualityErrorByDocument((prev) => ({ ...prev, [documentId]: hasQualityError }));
  };

  const { setDocumentUploadStatus, setBulkProcessing } = useUserContext();
  const { data: docs } = useGetDocuments(initialData.sessionId as number);
  // filter documents in both pendingUpload and selected.
  const documents: DocumentType[] =
    initialData.pendingUploadDocuments?.length > 0
      ? initialData.pendingUploadDocuments
      : (docs
          ?.filter((doc) => initialData.documentsSelected?.includes(doc.id))
          .map((doc) => ({
            documentId: doc.id,
            name: doc.name,
            guidelines: doc.guidelines,
            customerUploadRequired: doc.customerUploadRequired,
            files: initialData.failedUploadDocuments.find((pd) => pd.documentId === doc.id)?.files ?? [{ fileId: doc.id, content: undefined }],
          })) ?? []);

  const {
    control,
    clearErrors,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<UploadFilePageForm>({
    resolver: zodResolver(UploadFilePageSchema),
    mode: 'onSubmit',
    defaultValues: { ...initialData, pendingUploadDocuments: documents, uploadConfirmation: false },
  });

  const hasAnySubmittableFile = documents.some((doc) =>
    doc.files.some((file) => file.content && file.content.size > 0 && file.content.name !== FILE_DEFAULT_NAME),
  );
  const allDocumentsHaveFile =
    documents.length > 0 &&
    documents.every((doc) => doc.files.some((file) => file.content && file.content.size > 0 && file.content.name !== FILE_DEFAULT_NAME));
  const canSubmit = allDocumentsHaveFile && Boolean(watch('uploadConfirmation'));

  const postRequest = useCreateUploadAction();
  const invalidatePreviousAccess = useInvalidateAccess();

  const latestRequestedDocuments =
    docs?.filter((item) => item.documentStatus === DOCUMENT_STATUS.urlGenerated || item.documentStatus === DOCUMENT_STATUS.smsSent) ?? [];

  const handleDocumentUpdate = (document: DocumentType) => {
    const foundIndex = documents.findIndex((doc) => doc.documentId === document.documentId);
    if (foundIndex >= 0) documents[foundIndex] = document;
    setFormData((prev) => ({ ...prev, pendingUploadDocuments: documents }));

    // Enable global close button modal if any document has at least 1 file ready to upload
    const hasFilesWithContent = documents.some((doc) =>
      doc.files.some((file) => {
        return file.content && file.content?.size > 0;
      }),
    );

    setHasError(false);
    clearErrors();

    setDocumentUploadStatus(hasFilesWithContent ? DOCUMENT_UPLOAD_PROGRESS.inProgress : null);
    clearErrors();

    return documents;
  };

  const handleConfirmSubmit: SubmitHandler<UploadFilePageForm> = async (formData) => {
    queryClient.invalidateQueries({ queryKey: ['session', initialData.sessionId, 'documents'] });
    onNext(formData);
  };

  const onSubmit: SubmitHandler<UploadFilePageForm> = async () => {
    const uploadingDocuments = documents.map((doc) => ({
      ...doc,
      files: doc.files.filter((file) => file?.content?.name !== FILE_DEFAULT_NAME),
    }));

    const isOverrideRequestedDocuments = latestRequestedDocuments.some((document) =>
      uploadingDocuments.some((selected) => selected.documentId === document.id),
    );

    try {
      // Show global loader
      setBulkProcessing(true);

      if (isOverrideRequestedDocuments) {
        await invalidatePreviousAccess.mutateAsync({
          sessionId: Number(initialData.sessionId),
          formIds: uploadingDocuments.map((doc) => doc.documentId),
        });
      }
      const documentUploadPromiseMap = uploadingDocuments.map(async (document) => {
        return postRequest.mutateAsync({
          sessionId: initialData.sessionId as number,
          document,
        });
      });

      const results = await Promise.all(documentUploadPromiseMap);
      const documentsFailedIds = results.filter((result) => result.error).map((result) => result.document?.documentId);

      setDocumentFailedList(results.filter((result) => result.error).map((result) => result.document));

      if (documentsFailedIds.length === 0) {
        setIsSuccessModalOpen(true);
      } else {
        const { documentsSelected } = initialData;

        const allSelectedInErrors =
          documentsSelected.length === documentsFailedIds.length && documentsSelected.every((doc) => documentsFailedIds.includes(doc));
        const singleSelectedInErrors = documentsSelected.length === 1 && documentsFailedIds.includes(documentsSelected[0]);

        allSelectedInErrors || singleSelectedInErrors ? setIsErrorModalOpen(true) : setIsPartialErrorModalOpen(true);

        setValue('failedUploadDocuments', [
          // merge 2 list and make a new list of failed upload documents.
          ...uploadingDocuments.filter((document) => documentsFailedIds.includes(document.documentId)),
          ...initialData.failedUploadDocuments.filter((doc) => !documentsFailedIds.includes(doc.documentId)),
        ]);
        setValue('pendingUploadDocuments', []);

        setValue('documentsSelected', []);
      }
    } finally {
      // Hide global loader
      setBulkProcessing(false);
    }
  };

  const onError = () => {
    setHasError(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onReturnBack = () => {
    if (documents.some((document) => document.files?.some((file) => file.content && file.content.size > 0))) {
      setIsDeleteModalOpen(true);
      return;
    }
    onReturn();
  };

  const onReturn = () => {
    setFormData((prev) => ({
      ...prev,
      pendingUploadDocuments: [],
    }));
    // Reset global close button
    setDocumentUploadStatus(null);
    onBack();
  };

  const showValidationError = (document: DocumentType) => {
    if (document.files.length === 1) {
      return !document.files[0].content || document.files[0].content.size === 0;
    }

    return false;
  };

  return (
    <form className="flex flex-col gap-10" onSubmit={handleSubmit(onSubmit, onError)}>
      <StatusModal
        id="warning-delete-progress-modal"
        title="撮影したファイルはまだ送信されていません"
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        content="戻った場合、撮影・ファイル選択したファイルはすべて削除されますがよろしいでしょうか。"
        buttons={[
          {
            text: 'キャンセル',
          },
          {
            text: 'OK',
            onClick: () => {
              onReturn();
            },
          },
        ]}
      />

      <CustomModal
        open={isSuccessModalOpen}
        title="画像送信が完了しました"
        onClose={() => handleConfirmSubmit({ ...getValues(), pendingUploadDocuments: [], documentsSelected: [] })}
        buttonLabel="撮影方法選択画面へ"
      >
        <>
          <Text>画像の送信が完了しました。</Text>
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </>
      </CustomModal>
      <CustomModal
        open={isErrorModalOpen}
        title="送信に失敗しました"
        onClose={() => handleConfirmSubmit(getValues())}
        buttonLabel="撮影方法選択画面へ"
        isWarningModal
      >
        <>
          <Text>致命的なエラーが発生し、以下の帳票の送信に失敗しました。</Text>
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </>
      </CustomModal>
      <CustomModal
        open={isPartialErrorModalOpen}
        title="一部の送信に失敗しました"
        onClose={() => handleConfirmSubmit(getValues())}
        buttonLabel="撮影方法選択画面へ"
        isWarningModal
      >
        <div className="flex flex-col gap-5">
          <Text>エラーが発生し、以下の書類の送信に失敗しました。</Text>
          {documentFailedList?.map((documentFailed) => (
            <Text color="danger" key={`document-failed-waring${documentFailed?.documentId}`}>
              ・{documentFailed?.name}
            </Text>
          ))}
          <Text>撮影方法選択画面に戻り、ステータスを確認してください。</Text>
        </div>
      </CustomModal>

      <div className="flex flex-col gap-4">
        <Heading as="h1">書類の撮影・ファイル選択</Heading>
        {Object.keys(errors).length > 0 && errors.pendingUploadDocuments && (
          <Message title="入力内容をご確認ください" status="danger" hasCloseButton={false}>
            書類の撮影・ファイル選択をしてください。
          </Message>
        )}
        <div>
          <Text>撮影する場合は、1枚ずつ撮影してください。</Text>
          <Text>ファイル選択の場合は、画像（JPG・JPEG・PNG・TIFF）またはPDFのみ選択できます。</Text>
          <Text>
            PDF・TIFFのファイルを指定する場合、ファイル内に複数ページがあっても指定可能です。ただし、1ファイルに複数の書類の混在はできません。
          </Text>
        </div>
        <div className="bg-statusInformationLight p-7">
          {documents.map((document, index) => (
            <Heading key={`document-header-name-${document.documentId}`}>
              {index + 1}.{document.name}
            </Heading>
          ))}
        </div>
        <div className="flex flex-col">
          <div className="flex flex-col">
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              1画像・ファイルあたり20MB以下で撮影・選択してください。
            </Text>
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              1書類につき最大30ファイルまで撮影またはファイル選択いただけます。
            </Text>
            <Text className="flex flex-row">
              <span className="inline-block text-lg">
                <WarningIcon isOutline size="medium" />
              </span>
              撮影またはファイル選択した書類を確認してください。すべての書類の撮影またはファイルを選択後、送信画面に進んでください。
            </Text>
          </div>
        </div>
      </div>

      <Controller
        name={'pendingUploadDocuments'}
        control={control}
        render={({ field: { onChange } }) => (
          <>
            {documents.map((document, index) => {
              return (
                <DocumentUpload
                  key={document.documentId}
                  receptionNo={initialData.receptionNo}
                  document={document}
                  header={`${index + 1}. ${document.name}`}
                  onFileChange={(files) => {
                    onChange(handleDocumentUpdate({ ...document, files }));
                  }}
                  hasError={hasError && showValidationError(document)}
                  onQualityCheckChange={handleDocumentQualityCheckChange}
                />
              );
            })}
          </>
        )}
      />

      {hasAnySubmittableFile && (
        <div>
          <div className="flex w-full flex-row items-center gap-2">
            <Heading as="h3">確認してください</Heading>
            <Badge variant="important" text="必須" />
          </div>
          <ol className="bg-interactiveHoverGrey mb-4 p-2">
            <li>・複数ページ、裏面がある書類はすべてのページを撮影していますか？</li>
            <li>・画像が暗い、ブレている等で、文字が読めないことはありませんか？</li>
            <li>・帳票名と撮影画像に相違はないですか？</li>
            <li>・不要な画像があれば削除してください。</li>
            <li>・容量サイズが大きい場合は、送信に多くの時間を要することをご了承ください。</li>
          </ol>
          <CheckboxContainer checked={watch('uploadConfirmation')}>
            <Controller
              name={'uploadConfirmation'}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  showError={Boolean(errors['uploadConfirmation'])}
                  required={true}
                  onChange={field.onChange}
                  options={[
                    {
                      defaultChecked: field.value,
                      label: '上記の内容を確認しました。',
                      value: 'uploadConfirmation',
                    },
                  ]}
                />
              )}
            />
          </CheckboxContainer>
          {errors['uploadConfirmation'] && <ErrorMessage message={String(errors['uploadConfirmation']?.message)} />}
          {Object.values(qualityErrorByDocument).some(Boolean) && (
            <Message sx={{ 'margin-top': 4 }} status="danger" hasCloseButton={false} title="画像に問題がある可能性があります。">
              <Text>
                再度撮影される場合はファイルを削除してもう一度「書類を撮影」または「ファイルを選択」ボタンを押してください。この画像で問題ない場合は、「画像を送信する」ボタンを押してください。
              </Text>
            </Message>
          )}
        </div>
      )}

      <div className="flex justify-center gap-3 mt-5">
        <div className="w-80">
          <Button type="button" variant="outlined" onClick={() => onReturnBack()} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
            戻る
          </Button>
        </div>
        <div className="w-80">
          <Button type="submit" fullWidth icon={<ArrowIcon />} disabled={!canSubmit}>
            画像を送信する
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UploadFilePage;
